/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import {
	FilterApplication,
	FilterPageResponse
} from "../../../../models/http/dashboard/filter-page-response.model";

// ── 60 mock applications with rich layer sets ─────────────────────────────────
const LAYERS = [
	"Compute Engine",
	"Cloud Function",
	"GKE Cluster",
	"Cloud SQL Postgres",
	"Cloud SQL MySQL",
	"BigQuery",
	"Pub/Sub",
	"Cloud Storage",
	"Cloud Run",
	"App Engine",
	"Dataflow",
	"Dataproc",
	"Cloud Spanner",
	"Firestore",
	"Bigtable",
	"Memorystore Redis",
	"Cloud CDN",
	"Cloud Load Balancing",
	"Cloud DNS",
	"Cloud Armor"
];

function makeLayers(seed: number): string[] {
	// Each app gets a deterministic subset of layers based on seed
	return LAYERS.filter((_, i) => (seed * 7 + i * 3) % 5 < 2);
}

export const MOCK_APPLICATIONS: FilterApplication[] = Array.from({ length: 60 }, (_, i) => ({
	name: `Application ${String(i + 1).padStart(3, "0")}`,
	functionalUnit: ["API Calls", "Requests", "Data GB", "Active Users"][(i * 3) % 4]
}));

export const MOCK_LAYERS: Record<string, string[]> = Object.fromEntries(
	MOCK_APPLICATIONS.map((app, i) => [app.name, makeLayers(i)])
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildCursor(offset: number): string {
	return btoa(String(offset));
}

function parseCursor(cursor: string | null | undefined): number {
	if (!cursor) return 0;
	try {
		return parseInt(atob(cursor), 10);
	} catch {
		return 0;
	}
}

/**
 * Generic in-memory paginator that mirrors the backend cursor-pagination contract.
 */
export function paginate<T>(
	items: T[],
	cursor: string | null | undefined,
	size: number
): FilterPageResponse<T> {
	const offset = parseCursor(cursor);
	const page = items.slice(offset, offset + size);
	const nextOffset = offset + page.length;
	const hasNext = nextOffset < items.length;

	return {
		content: page,
		nextCursor: hasNext ? buildCursor(nextOffset) : null,
		previousCursor: offset > 0 ? buildCursor(Math.max(0, offset - size)) : null,
		hasNext,
		hasPrevious: offset > 0,
		size,
		numberOfElements: page.length,
		empty: page.length === 0
	};
}
